import type { TicketMessage } from '@/entities/ticket';
import { resolveApiUrl } from '@/shared/lib/config';

export const TYPING_TIMEOUT_MS = 3000;
export const MAX_ATTACHMENTS = 6;
export const MESSAGE_MUTATION_WINDOW_MS = 24 * 60 * 60 * 1000;

export type DeleteRequest =
  | {
      kind: 'bulk';
      messageIds: string[];
      attachments: Array<{ messageId: string; attachmentId: string }>;
    }
  | { kind: 'attachment'; messageId: string; attachmentId: string };

export interface ReplyTarget {
  messageId: string;
  attachmentId?: string;
}

export interface PendingFile {
  localId: string;
  file: File;
  previewUrl: string | null;
  progress: number;
  status: 'ready' | 'uploading' | 'error';
  error?: string;
}

export interface MessageGroup<M> {
  items: M[];
  dayStart: boolean;
  unreadStart: boolean;
}

export function groupMessages<M extends { id: string; authorId: string; createdAt: string }>(
  messages: M[],
  unreadStartId: string | null,
): MessageGroup<M>[] {
  const groups: MessageGroup<M>[] = [];
  let prev: M | undefined;
  for (const m of messages) {
    const dayStart =
      !prev || new Date(prev.createdAt).toDateString() !== new Date(m.createdAt).toDateString();
    const unreadStart = m.id === unreadStartId;
    if (!prev || dayStart || unreadStart || prev.authorId !== m.authorId) {
      groups.push({ items: [m], dayStart, unreadStart });
    } else {
      groups[groups.length - 1].items.push(m);
    }
    prev = m;
  }
  return groups;
}

export const attachmentKey = (messageId: string, attachmentId: string) =>
  `${messageId}|${attachmentId}`;

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

export function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/') && mimeType.toLowerCase() !== 'image/svg+xml';
}

export function isVideo(mimeType: string): boolean {
  return mimeType.startsWith('video/');
}

export function describeReplyTarget(
  messages: TicketMessage[],
  currentUserId: string,
  target: { messageId: string; attachmentId?: string | null },
): { author: string; text: string; thumbUrl: string | null } {
  const quoted = messages.find((m) => m.id === target.messageId);
  if (!quoted) return { author: 'Сообщение недоступно', text: '', thumbUrl: null };
  const author =
    quoted.authorId === currentUserId ? 'Вы' : quoted.author?.displayName ?? 'Собеседник';
  const attachment = target.attachmentId
    ? (quoted.attachments ?? []).find((a) => a.id === target.attachmentId)
    : undefined;
  if (attachment) {
    return {
      author,
      text: isImage(attachment.mimeType) ? 'Фото' : attachment.fileName,
      thumbUrl: isImage(attachment.mimeType)
        ? resolveApiUrl(attachment.thumbUrl ?? attachment.url)
        : null,
    };
  }
  const text =
    quoted.body.trim() ||
    ((quoted.attachments?.length ?? 0) > 0
      ? quoted.attachments!.some((a) => isImage(a.mimeType))
        ? 'Фото'
        : 'Файл'
      : '');
  const firstImage = quoted.attachments?.find((a) => isImage(a.mimeType));
  return {
    author,
    text: text.length > 90 ? `${text.slice(0, 87)}…` : text,
    thumbUrl:
      !quoted.body.trim() && firstImage
        ? resolveApiUrl(firstImage.thumbUrl ?? firstImage.url)
        : null,
  };
}
